/**
 * @file inward-grn-decisions.tsx
 * @module app/modules/inward/components/inward-grn-decisions
 * @description Warehouse / Finance decisions on an Inward GRN:
 *   - Missing / damaged quantities are highlighted; each is approved (closed on the PO) or
 *     rejected with a note (it goes back to the PO as pending, for a new GRN). The GRN cannot
 *     be approved until every one is decided.
 *   - Reject GRN (Inward right "Reject GRN"; reason required), only while nothing of it is live in stock: all of its
 *     quantities go back to the PO as pending and a new GRN can be created.
 *   - A rejected GRN shows who rejected it, when and why.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.4.0
 * @version 1.4.0
 */

import { useState } from "react";
import { Badge, Button, Card, Form, Table } from "react-bootstrap";
import { INWARD } from "../../../../api/apiEndPoints";
import { error, success } from "../../../../Global/toast";
import {
  canRejectGrn,
  InwardGrnRecord,
  shortfallItems,
  ShortfallItem,
  SHORTFALL_APPROVED,
  SHORTFALL_PENDING,
  SHORTFALL_REJECTED,
} from "../utils/inward-approval";
import { UNIT_LABELS } from "../utils/inward-po";
import { formatDateTime } from "../utils/inward-timeline";

interface InwardGrnDecisionsProps {
  grn: InwardGrnRecord;
  /** user has Warehouse or Finance approval (or is super admin): decides missing / damaged */
  canDecide: boolean;
  /** user has the Inward "Reject GRN" right (or is super admin) */
  canReject: boolean;
  call: (endpoint: string, params: unknown, path?: unknown) => Promise<any>;
  /** reload the GRN after a decision */
  onChanged: () => Promise<void> | void;
}

const STAGE = { warehouse: "Warehouse", finance: "Finance" } as Record<
  string,
  string
>;

const statusBadge = (s: ShortfallItem) =>
  s.status === SHORTFALL_APPROVED
    ? { label: "Approved", variant: "success" }
    : s.status === SHORTFALL_REJECTED
    ? { label: "Rejected · back to PO", variant: "danger" }
    : s.status === SHORTFALL_PENDING
    ? { label: "Decision needed", variant: "warning" }
    : { label: "Recorded", variant: "neutral" };

const InwardGrnDecisions = ({
  grn,
  canDecide,
  canReject,
  call,
  onChanged,
}: InwardGrnDecisionsProps) => {
  const [busy, setBusy] = useState("");
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState("");

  const items = shortfallItems(grn);
  const rejection = grn.inward?.rejection;
  const pending = items.filter((s) => s.status === SHORTFALL_PENDING);

  const decide = async (s: ShortfallItem, decision: "approve" | "reject") => {
    const note = (notes[s.key] ?? "").trim();
    if (decision === "reject" && !note) {
      error(`Enter why the missing / damaged of ${s.title} is rejected`);
      return;
    }
    setBusy(s.key);
    const response = await call(
      INWARD.decideShortfall,
      { variant: s.variant, type: s.type, decision, note },
      { id: grn._id, a: "shortfall" }
    );
    setBusy("");
    if (!response) return;
    success(
      decision === "approve"
        ? `Missing / damaged of ${s.title} approved`
        : `Missing / damaged of ${s.title} rejected: ${
            s.missing + s.damaged
          } back to the PO as pending`
    );
    await onChanged();
  };

  const rejectGrn = async () => {
    if (!reason.trim()) {
      error("Enter the reason for rejecting this GRN");
      return;
    }
    setBusy("grn");
    const response = await call(
      INWARD.rejectGrn,
      { reason: reason.trim() },
      { id: grn._id, a: "reject" }
    );
    setBusy("");
    if (!response) return;
    setRejecting(false);
    setReason("");
    success(
      `GRN-${grn.refKey} rejected. Its quantities are pending on PO ${
        grn.poRefKey || ""
      } again; a new GRN can be created.`
    );
    await onChanged();
  };

  if (rejection) {
    return (
      <div className="alert alert-danger mb-5" data-testid="grn-rejected">
        <div className="fs-16 fw-bold mb-1">
          GRN rejected by {rejection.by?.name || "-"}
          {rejection.stage
            ? ` (${STAGE[rejection.stage] ?? rejection.stage})`
            : ""}{" "}
          on {formatDateTime(rejection.at)}
        </div>
        <div className="fs-15">Reason: {rejection.reason || "-"}</div>
        <div className="fs-14 mt-1">
          Its quantities went back to the PO as pending; a new GRN can be
          created for them.
        </div>
      </div>
    );
  }

  return (
    <>
      {items.length > 0 && (
        <Card
          className={`inward-card mb-5 ${
            pending.length ? "border border-warning" : ""
          }`}
          data-testid="grn-shortfalls"
        >
          <Card.Body>
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
              <h2 className="fs-18 fw-bold mb-0">Missing / damaged</h2>
              {pending.length > 0 && (
                <Badge
                  bg=""
                  className="inward-badge inward-badge--warning fs-14 py-2 px-3"
                  data-testid="grn-shortfalls-pending"
                >
                  {pending.length} to approve or reject
                </Badge>
              )}
            </div>
            <p className="fs-14 text-muted mb-3">
              Approve: the quantity is closed on the PO. Reject: it goes back to
              the PO as pending, for a new GRN. The GRN can be approved once
              every line is decided.
            </p>
            <div className="table-responsive">
              <Table className="align-middle mb-0">
                <thead>
                  <tr className="fs-14 fw-bold text-muted">
                    <th>Product</th>
                    <th className="text-center">Missing</th>
                    <th className="text-center">Damaged</th>
                    <th>Decision</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((s) => {
                    const badge = statusBadge(s);
                    const open = s.status === SHORTFALL_PENDING;
                    return (
                      <tr key={s.key} data-testid="grn-shortfall-row">
                        <td className="fs-15 fw-600">
                          {s.title} ({UNIT_LABELS[s.type]?.short ?? s.type})
                        </td>
                        <td className="text-center fs-15">{s.missing}</td>
                        <td className="text-center fs-15">{s.damaged}</td>
                        <td>
                          <Badge
                            bg=""
                            className={`inward-badge inward-badge--${badge.variant} fs-13 py-2 px-3`}
                          >
                            {badge.label}
                          </Badge>
                          {!open && s.by && (
                            <div className="fs-13 text-muted mt-1">
                              {s.by}
                              {s.stage
                                ? ` (${STAGE[s.stage] ?? s.stage})`
                                : ""}{" "}
                              · {formatDateTime(s.at)}
                              {s.note ? ` · ${s.note}` : ""}
                            </div>
                          )}
                          {open && canDecide && (
                            <div className="d-flex flex-wrap gap-2 mt-2">
                              <Form.Control
                                className="inward-input"
                                style={{ maxWidth: 260 }}
                                placeholder="Note (needed to reject)"
                                value={notes[s.key] ?? ""}
                                onChange={(e) =>
                                  setNotes((prev) => ({
                                    ...prev,
                                    [s.key]: e.target.value,
                                  }))
                                }
                                data-testid="grn-shortfall-note"
                              />
                              <Button
                                variant="success"
                                className="inward-touch-btn"
                                disabled={!!busy}
                                onClick={() => decide(s, "approve")}
                                data-testid="grn-shortfall-approve"
                              >
                                Approve
                              </Button>
                              <Button
                                variant="outline-danger"
                                className="inward-touch-btn"
                                disabled={!!busy}
                                onClick={() => decide(s, "reject")}
                                data-testid="grn-shortfall-reject"
                              >
                                Reject · back to PO
                              </Button>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          </Card.Body>
        </Card>
      )}

      {canReject && canRejectGrn(grn) && (
        <Card className="inward-card mb-5" data-testid="grn-reject">
          <Card.Body>
            {!rejecting ? (
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
                <span className="fs-15">
                  Wrong quantities on this GRN? Reject it: everything on it goes
                  back to the PO as pending and a new GRN can be created.
                </span>
                <Button
                  variant="outline-danger"
                  className="inward-touch-btn"
                  onClick={() => setRejecting(true)}
                  data-testid="grn-reject-open"
                >
                  Reject GRN
                </Button>
              </div>
            ) : (
              <div className="d-flex flex-column gap-3">
                <Form.Group>
                  <Form.Label className="fs-15 fw-bold">
                    Reason for rejecting GRN-{grn.refKey}
                  </Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    className="inward-input"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    data-testid="grn-reject-reason"
                  />
                </Form.Group>
                <div className="d-flex gap-2 flex-wrap">
                  <Button
                    variant="danger"
                    className="inward-touch-btn"
                    disabled={busy === "grn"}
                    onClick={rejectGrn}
                    data-testid="grn-reject-confirm"
                  >
                    {busy === "grn" ? "Rejecting…" : "Reject GRN"}
                  </Button>
                  <Button
                    variant="outline-secondary"
                    className="inward-touch-btn"
                    disabled={busy === "grn"}
                    onClick={() => {
                      setRejecting(false);
                      setReason("");
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </Card.Body>
        </Card>
      )}
    </>
  );
};

export default InwardGrnDecisions;
